// The mentor has 90 seconds. A background tab still has to reach them: a
// ring every 3 seconds until they answer, a flashing tab title and, when the
// tab is hidden, a browser notification (permission is asked when they go live).

let titleTimer: number | null = null
let originalTitle = ''

const RING_EVERY_SECONDS = 3
// One ring is two quick pairs of notes, like a phone: [frequency, offset].
const RING_NOTES: [number, number][] = [
  [880, 0],
  [1320, 0.16],
  [880, 0.5],
  [1320, 0.66],
]
const RING_VOLUME = 0.35

// One audio context for the page. A new context per ring would pass the
// browser's limit within one request.
let audio: AudioContext | null = null
let ringOutput: GainNode | null = null

const audioContext = () => {
  audio ??= new AudioContext()
  return audio
}

// Browsers start audio suspended until the person clicks on the page. The
// go-live click and the first click on any page resume it.
export const unlockAudio = () => {
  try {
    const context = audioContext()
    if (context.state === 'suspended') context.resume()
  } catch {
    // No Web Audio. The card and the title still show.
  }
}

const planRing = (context: AudioContext, output: GainNode, at: number) => {
  RING_NOTES.forEach(([frequency, offset]) => {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.value = frequency
    const start = at + offset
    gain.gain.setValueAtTime(0.0001, start)
    gain.gain.exponentialRampToValueAtTime(RING_VOLUME, start + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3)
    oscillator.connect(gain).connect(output)
    oscillator.start(start)
    oscillator.stop(start + 0.35)
  })
}

// Every ring up to the timeout goes on the audio clock at once. Page timers
// slow down in a background tab; the audio clock does not.
export const startRing = (expiresAt: string) => {
  stopRing()
  try {
    const context = audioContext()
    if (context.state === 'suspended') context.resume()
    const output = context.createGain()
    output.connect(context.destination)
    ringOutput = output
    const seconds = (new Date(expiresAt).getTime() - Date.now()) / 1000
    const rings = Math.max(1, Math.ceil(seconds / RING_EVERY_SECONDS))
    for (let index = 0; index < rings; index++) {
      planRing(
        context,
        output,
        context.currentTime + index * RING_EVERY_SECONDS,
      )
    }
  } catch {
    // Audio can be blocked before the first click on the page. The card
    // still shakes and the title still flashes.
  }
}

// Cutting the one output silences every ring still planned.
export const stopRing = () => {
  ringOutput?.disconnect()
  ringOutput = null
}

export const startTitleFlash = (label: () => string) => {
  stopTitleFlash()
  originalTitle = document.title
  let on = false
  titleTimer = window.setInterval(() => {
    on = !on
    document.title = on ? label() : originalTitle
  }, 1000)
}

export const stopTitleFlash = () => {
  if (titleTimer !== null) {
    window.clearInterval(titleTimer)
    titleTimer = null
    document.title = originalTitle || document.title
  }
}

export const askNotificationPermission = () => {
  if (!('Notification' in window)) return
  if (Notification.permission === 'default') Notification.requestPermission()
}

export const showBrowserNotification = (title: string, body: string) => {
  if (!('Notification' in window)) return
  if (Notification.permission !== 'granted' || !document.hidden) return
  const notification = new Notification(title, { body, tag: 'instant-request' })
  notification.onclick = () => {
    window.focus()
    notification.close()
  }
}
